import moment from 'moment';
import React from 'react';
import theme from '../../../../../../assets/styles/theme/theme';
import {
  EOrderType,
  IFutureDelivery,
  IOpenOrderData
} from '../../../../../../store/ducks/long-steel-orders/types';
import { formatCurrency } from '../../../../../../utils/formatNumbers';
import {
  excelNumberCell,
  IExcelNumberCell,
  isExcelNumberCell
} from '../../../../../../utils/createXMLTable';

interface IOpenOrderTableToExcelProps {
  orders: IOpenOrderData[];
  hasSomeFlatItem: boolean;
}

interface ITitleTd {
  title: string;
  width: number;
  backgroundColor: string;
}

interface ITableTr {
  tdsContent: Array<React.Key | IExcelNumberCell>;
}

const OpenOrderTableContentToExcel: React.FC<IOpenOrderTableToExcelProps> = ({
  orders,
  hasSomeFlatItem
}) => {
  const headerBackgroundColorBlue = theme.colors.blue2;
  const trackingHeaderColor = theme.colors.yellow;
  const evenRowColor = theme.colors.white3;

  const getCurrencyValue = (value: string): string => {
    if (value === 'Indisponível') {
      return ' - ';
    } else {
      return formatCurrency(value);
    }
  };

  const isAllItemsFlatSteel = !orders.some(
    (item) => item.orderType !== EOrderType.PLANOS
  );

  const hasSomeIndustryItem = orders.some((item) =>
    ['BRIN', 'BRIO'].includes(item.salesOrg)
  );

  const canShowDeliveryDateColunm =
    !isAllItemsFlatSteel && !hasSomeIndustryItem;

  const getTitleTds = (): ITitleTd[] => {
    const titleTds: ITitleTd[] = [];

    const addTitleTd = (
      title: string,
      width = 100,
      backgroundColor = headerBackgroundColorBlue
    ) => {
      titleTds.push({ title, width, backgroundColor });
    };

    addTitleTd('Emissor', 120);
    addTitleTd('Recebedor', 120);
    addTitleTd('Nº OV Gerdau', 80);
    addTitleTd('Nº Item', 80);
    addTitleTd('Nº do Pedido', 80);
    addTitleTd('Planta', 190);
    addTitleTd('Material cliente', 80);
    addTitleTd('Material Gerdau');
    addTitleTd('Descrição do produto', 250);
    addTitleTd('Criação do pedido');
    addTitleTd('Data Solicitada');

    if (canShowDeliveryDateColunm) {
      addTitleTd('Data de Remessa');
    }

    addTitleTd('Qtd. Solicitada');
    addTitleTd('Un. Solicitada');
    addTitleTd('Qtd. Solicitada (TON)');
    addTitleTd('Qtd. Faturada (TON)');
    addTitleTd('Qtd. Em Remessa (TON)');
    addTitleTd('Qtd. Em Carregamento (TON)');
    addTitleTd('Saldo (TON)');
    addTitleTd('Condição de pagamento');
    addTitleTd('Preço unitário c/ impostos');
    addTitleTd('Preço unitário');
    addTitleTd('Preço Total s/ Impostos');
    addTitleTd('Preço total c/ impostos');

    if (hasSomeFlatItem) {
      addTitleTd('Aciaria (TON)', 100, trackingHeaderColor);
      addTitleTd('Laminação (TON)', 100, trackingHeaderColor);
      addTitleTd('LA (TON)', 100, trackingHeaderColor);
      addTitleTd('Acabamento (TON)', 100, trackingHeaderColor);
      addTitleTd('Estoque Usina (TON)', 100, trackingHeaderColor);
      addTitleTd('Em Transferência (TON)', 100, trackingHeaderColor);
      addTitleTd('Estoque Entre-Posto (TON)', 100, trackingHeaderColor);
    }

    let totalH = 0;
    orders.forEach((item) => {
      if (item.futureDeliveries.length > totalH)
        totalH = item.futureDeliveries.length;
    });

    for (let i = 1; i <= totalH; i++) {
      addTitleTd(`Data estimada - ${i}`);
      addTitleTd(`Qtd. Entrega ${i}`);
      addTitleTd(`Un. Entrega ${i}`, 80);
    }

    return titleTds;
  };
  const allTitleTds = getTitleTds();

  const getTableLines = (): ITableTr[] => {
    const trs: ITableTr[] = [];

    orders.forEach((item) => {
      const tdsContent: Array<React.Key | IExcelNumberCell> = [];
      tdsContent.push(
        `${item.soldTo.name} - ${item.soldTo.address}, ${item.soldTo.city} - ${item.soldTo.state}`
      );
      tdsContent.push(
        `${item.shipTo.name} - ${item.shipTo.address}, ${item.shipTo.city} - ${item.shipTo.state}`
      );
      tdsContent.push(item.salesOrder);
      tdsContent.push(item.salesOrderItem);
      tdsContent.push(item.customerOrder);
      tdsContent.push(item.plantDescription);
      tdsContent.push(item.customerSKU);
      tdsContent.push(item.sku);
      tdsContent.push(item.materialDescription || '');
      tdsContent.push(moment(item.itemCreationDate).format('DD/MM/YYYY'));
      tdsContent.push(moment(item.requestedDate).format('DD/MM/YYYY'));

      if (canShowDeliveryDateColunm) {
        tdsContent.push(
          item.quantities.deliveryDate
            ? moment(item.quantities.deliveryDate).format('DD/MM/YYYY')
            : '-'
        );
      }

      tdsContent.push(
        excelNumberCell(item.quantities.requestedQuantity) ?? ' - '
      );
      tdsContent.push(item.quantities.requestedMeasureUnit);
      tdsContent.push(
        excelNumberCell(item.quantities.requestedConversionQuantity) ?? ' - '
      );
      tdsContent.push(
        excelNumberCell(item.quantities.billedConversionQuantity) ?? ' - '
      );
      tdsContent.push(
        excelNumberCell(item.quantities.deliveryConversionQuantity) ?? ' - '
      );
      tdsContent.push(
        excelNumberCell(item.quantities.shipmentConversionQuantity) ?? ' - '
      );
      tdsContent.push(
        excelNumberCell(item.quantities.pendingConversionQuantity) ?? ' - '
      );
      tdsContent.push(item.conditions.paymentTermsDescription);
      tdsContent.push(getCurrencyValue(item.conditions.unitPriceWithTaxes));
      tdsContent.push(getCurrencyValue(item.conditions.unitPriceWithoutTaxes));
      tdsContent.push(getCurrencyValue(item.conditions.totalValueWithoutTaxes));
      tdsContent.push(getCurrencyValue(item.conditions.totalValueWithTaxes));

      if (hasSomeFlatItem) {
        const addFlatItem = (
          flatItemValue?: number
        ): IExcelNumberCell | string =>
          excelNumberCell(flatItemValue ?? 0) ?? ' - ';

        const isCurrentItemFlatSteel = item.orderType === EOrderType.PLANOS;
        if (isCurrentItemFlatSteel) {
          tdsContent.push(addFlatItem(item.trackingFlatsteelExcel.meltShop));
          tdsContent.push(addFlatItem(item.trackingFlatsteelExcel.rollingMill));
          tdsContent.push(
            addFlatItem(item.trackingFlatsteelExcel.rollingFinish)
          );
          tdsContent.push(addFlatItem(item.trackingFlatsteelExcel.finish));
          tdsContent.push(addFlatItem(item.trackingFlatsteelExcel.stockMill));
          tdsContent.push(addFlatItem(item.trackingFlatsteelExcel.transit));
          tdsContent.push(
            addFlatItem(item.trackingFlatsteelExcel.stockWarehouse)
          );
        } else {
          tdsContent.push(' - ', ' - ', ' - ', ' - ', ' - ', ' - ', ' - ');
        }
      }

      if (item.futureDeliveries.length > 0) {
        item.futureDeliveries.forEach((delivery: IFutureDelivery) => {
          tdsContent.push(delivery.estimatedDeliveryDate);
          tdsContent.push(
            excelNumberCell(delivery.estimatedDeliveryQuantity) ?? ' - '
          );
          tdsContent.push(delivery.estimatedDeliveryMeasureUnit);
        });
      } else {
        tdsContent.push(
          'Pedido não confirmado. Consultar Gerdau.',
          ' - ',
          ' - '
        );
      }

      trs.push({ tdsContent });
    });

    return trs;
  };
  const allTrs = getTableLines();

  return (
    <tbody>
      {hasSomeFlatItem && (
        <tr style={{ fontSize: '14px', fontWeight: 'bold' }}>
          <th
            align="center"
            colSpan={canShowDeliveryDateColunm ? 24 : 23}
            style={{ background: headerBackgroundColorBlue }}
          ></th>
          <th
            align="center"
            colSpan={7}
            style={{ background: trackingHeaderColor }}
          >
            TRACKING DO PEDIDO
          </th>
          <th
            align="center"
            colSpan={3}
            style={{ background: headerBackgroundColorBlue }}
          ></th>
        </tr>
      )}

      <tr
        style={{
          fontSize: '14px',
          verticalAlign: 'middle',
          whiteSpace: 'pre-wrap'
        }}
      >
        {allTitleTds.map((td) => (
          <td
            key={td.title}
            align="center"
            style={{
              height: `80px`,
              width: `${td.width}px`,
              background: td.backgroundColor
            }}
          >
            {td.title}
          </td>
        ))}
      </tr>

      {allTrs.map((trs, idx) => (
        <tr key={idx}>
          {trs.tdsContent.map((td, tIndex) => {
            const numberCell = isExcelNumberCell(td) ? td : null;
            return (
              <td
                key={`${idx}-${tIndex}`}
                data-excel-number={numberCell?.value}
                style={{
                  whiteSpace: 'nowrap',
                  fontSize: '14px',
                  background: idx & 1 ? evenRowColor : 'white'
                }}
              >
                {numberCell ? numberCell.value : td}
              </td>
            );
          })}
        </tr>
      ))}
    </tbody>
  );
};

export default OpenOrderTableContentToExcel;
